import React, { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import { RootState } from 'modules';
import { getMenteeCreditTransactions } from 'libs/api/user';
import { useDataApi, usePagination, useTimezone } from 'libs/hooks';
import {
  EMentoringTicketBackendNaming,
  CMentoringTicketDepositKeyname,
  CMentoringTicketWithdrawKeyname,
  CFinanceMentoringTicketDepositKeyname,
  CFinanceMentoringTicketWithdrawKeyname,
} from 'modules/mentoring';
import { TListResponse, TPagination } from 'modules/types';
import { TCreditTransaction, TUser } from 'modules/user';
import { CWindowWidth, CDateFormat } from 'constants/base.const';
import { ECreditHistoryStatus } from 'enums/mentoring.enum';
import { EMentoringField } from 'enums/mentoring.enum';
import { ERoute } from 'enums/route.enum';
import { List, Tooltip } from 'antd';
import styled from 'styled-components';
import { PrimaryColor, SecondaryColor, Colors, device } from 'GlobalStyles';

const MentoringCourseTicketHistory = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  const [pagination, setPagination] = usePagination(10);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromise = getMenteeCreditTransactions.bind(null, {
    field: {
      mentoring_field: currentUser?.primary_field,
    },
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
    },
  });

  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TCreditTransaction>>(getPromise, {
    contents: [],
    last: false,
  });
  const [getDateTime, timezone] = useTimezone();

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  //티켓 추가 개수 산출
  const getTicketDeposit = (item: any, mentoringName: string) => {
    let deposit;
    if (currentUser?.primary_field === EMentoringField.Finance) {
      deposit = String(get(item, CFinanceMentoringTicketDepositKeyname[mentoringName])).replace('.0', '');
      return deposit;
    }
    if (currentUser?.primary_field === EMentoringField.Consulting) {
      if (mentoringName === EMentoringTicketBackendNaming.MockInterviewPackage) {
        deposit =
          Number(String(get(item, 'junior_deposit')).replace('.0', '')) +
          Number(String(get(item, 'senior_deposit')).replace('.0', ''));
        return deposit;
      } else if (mentoringName === EMentoringTicketBackendNaming.TutorialPackage) {
        deposit =
          Number(String(get(item, 'special_deposit')).replace('.0', '')) +
          Number(String(get(item, 'senior_special_deposit')).replace('.0', ''));
        return deposit;
      } else {
        deposit = String(get(item, CMentoringTicketDepositKeyname[mentoringName])).replace('.0', '');
        return deposit;
      }
    }
  };

  //티켓 사용 개수 산출
  const getTicketWithdraw = (item: any, mentoringName: string) => {
    let withdraw;
    if (currentUser?.primary_field === EMentoringField.Finance) {
      withdraw = String(get(item, CFinanceMentoringTicketWithdrawKeyname[mentoringName])).replace('.0', '');
      return withdraw;
    }
    if (currentUser?.primary_field === EMentoringField.Consulting) {
      if (mentoringName === EMentoringTicketBackendNaming.MockInterviewPackage) {
        withdraw =
          Number(String(get(item, 'junior_withdraw')).replace('.0', '')) +
          Number(String(get(item, 'senior_withdraw')).replace('.0', ''));
        return withdraw;
      } else if (mentoringName === EMentoringTicketBackendNaming.TutorialPackage) {
        withdraw =
          Number(String(get(item, 'special_withdraw')).replace('.0', '')) +
          Number(String(get(item, 'senior_special_withdraw')).replace('.0', ''));
        return withdraw;
      } else {
        withdraw = String(get(item, CMentoringTicketWithdrawKeyname[mentoringName])).replace('.0', '');
        return withdraw;
      }
    }
  };

  return (
    <StyledMentoringHistoryWrapper
      style={{
        maxWidth: `${CWindowWidth - 400}px`,
        padding: '20px 10px',
        margin: 'auto',
      }}
    >
      <div
        style={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: SecondaryColor,
        }}
      >
        멘토링 수강권
      </div>

      <List
        pagination={{
          ...pagination,
          total: get(data, 'total', 0),
          position: 'bottom',
          hideOnSinglePage: true,
          onChange: (page) => {
            reloadPage({
              current: page,
              pageSize: pagination.pageSize,
            });
          },
        }}
        loading={loading}
        dataSource={get(data, 'contents')}
        renderItem={(item) => {
          return (
            <StyledListItem>
              {/* status가 'purchase' / 'nonattendance' / 'penalty' */}
              {(get(item, 'status') == ECreditHistoryStatus.Purchase ||
                get(item, 'status') == ECreditHistoryStatus.Nonattendance ||
                get(item, 'status') == ECreditHistoryStatus.Penalty) && (
                <Tooltip
                  title={
                    <>
                      {get(item, 'status') === ECreditHistoryStatus.Purchase && <span>수강권 구입</span>}
                      {get(item, 'status') === ECreditHistoryStatus.Nonattendance && (
                        <span>
                          멘토의 수업불참으로 인한 환불
                          <div>
                            <Link
                              to={`${ERoute.MentoringClassView.replace(
                                ':record_id',
                                String(get(item, 'mentoring_class_id')),
                              )}`}
                            >
                              <span style={{ color: '#fff', fontWeight: 'bold' }}>수업 자세히보기</span>
                            </Link>
                          </div>
                        </span>
                      )}
                      {get(item, 'status') === ECreditHistoryStatus.Penalty && (
                        <span>
                          멘토의 수업불참 패널티 지급
                          <div>
                            <Link
                              to={`${ERoute.MentoringClassView.replace(
                                ':record_id',
                                String(get(item, 'mentoring_class_id')),
                              )}`}
                            >
                              <span style={{ color: '#fff', fontWeight: 'bold' }}>수업 자세히보기</span>
                            </Link>
                          </div>
                        </span>
                      )}
                    </>
                  }
                  placement='topLeft'
                >
                  <StyledListItemContents>
                    <div>
                      <span className='created_at'>
                        {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                      </span>

                      <span
                        style={{
                          color: `${SecondaryColor}`,
                          fontWeight: 'bold',
                        }}
                      >
                        {/* status가 'purchase' / 'nonattendance' / 'penalty' 일 때는 크레딧 추가 */}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;추가
                      </span>
                      <StyledMentoringTicketName>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {get(item, 'mentoring_ticket_name')}
                      </StyledMentoringTicketName>
                    </div>
                    {/* &nbsp;&nbsp;&nbsp;
                    {String(get(item, 'deposit')).replace('.0', '')}회 */}
                    <div>
                      <span>
                        &nbsp;&nbsp;&nbsp;
                        {getTicketDeposit(item, get(item, 'mentoring_ticket_name'))}회
                        {/* {String(get(item, 'withdraw')).replace('.0', '')}회 */}
                      </span>
                      <StyledWithMentorName className='ellipsis-line-1'>
                        &nbsp;&nbsp;&nbsp;
                        {get(item, 'with_mentor')}
                      </StyledWithMentorName>
                    </div>
                  </StyledListItemContents>
                </Tooltip>
              )}
              {/* status가 'mentoring'*/}
              {get(item, 'status') === ECreditHistoryStatus.Mentoring && (
                <Tooltip
                  title={
                    <Link
                      to={`${ERoute.MentoringClassView.replace(
                        ':record_id',
                        String(get(item, 'mentoring_class_id')),
                      )}`}
                    >
                      <span style={{ color: '#fff', fontWeight: 'bold' }}>수업 자세히보기</span>
                    </Link>
                  }
                  placement='topLeft'
                >
                  <StyledListItemContents>
                    <div>
                      <span className='created_at'>
                        {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                      </span>
                      <span style={{ color: `${PrimaryColor}`, fontWeight: 'bold' }}>
                        {/* status가 'mentoring'은 크레딧 사용*/}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;사용
                      </span>
                      <StyledMentoringTicketName>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {get(item, 'mentoring_ticket_name')}
                      </StyledMentoringTicketName>
                    </div>
                    <div>
                      <span>
                        &nbsp;&nbsp;&nbsp;
                        {/* {String(get(item, 'withdraw')).replace('.0', '')}회 */}
                        {getTicketWithdraw(item, get(item, 'mentoring_ticket_name'))}회
                      </span>
                      <StyledWithMentorName className='ellipsis-line-1'>
                        &nbsp;&nbsp;&nbsp;
                        {get(item, 'with_mentor')}
                      </StyledWithMentorName>
                    </div>
                  </StyledListItemContents>
                </Tooltip>
              )}
              {/* status가 'offline' ==> 오프라인 수업*/}
              {get(item, 'status') === ECreditHistoryStatus.Offline && (
                <Tooltip title='오프라인 멘토링' placement='topLeft'>
                  <StyledListItemContents>
                    <div>
                      <span className='created_at'>
                        {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                      </span>
                      <span style={{ color: `${PrimaryColor}`, fontWeight: 'bold' }}>
                        {/*  status가 'offline' ==> 오프라인 수업 은 크레딧 사용*/}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;사용
                      </span>
                      <StyledMentoringTicketName>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {get(item, 'mentoring_ticket_name')}
                      </StyledMentoringTicketName>
                    </div>
                    <div>
                      <span>
                        &nbsp;&nbsp;&nbsp;
                        {getTicketWithdraw(item, get(item, 'mentoring_ticket_name'))}회
                      </span>
                      <StyledWithMentorName className='ellipsis-line-1'>
                        &nbsp;&nbsp;&nbsp;
                      </StyledWithMentorName>
                    </div>
                  </StyledListItemContents>
                </Tooltip>
              )}
              {get(item, 'status') === ECreditHistoryStatus.AddedByAdmin && (
                // <Tooltip title="오프라인 멘토링" placement="topLeft">
                <StyledListItemContents>
                  <div>
                    <span className='created_at'>
                      {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                    </span>
                    <span style={{ color: `${SecondaryColor}`, fontWeight: 'bold' }}>
                      {/* status가 'offline' ==> 오프라인 수업 은 크레딧 사용*/}
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;추가
                    </span>
                    <StyledMentoringTicketName>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      {get(item, 'mentoring_ticket_name')}
                    </StyledMentoringTicketName>
                  </div>
                  <div>
                    <span>
                      &nbsp;&nbsp;&nbsp;
                      {getTicketDeposit(item, get(item, 'mentoring_ticket_name'))}회
                    </span>
                    <StyledWithMentorName className='ellipsis-line-1'>
                      &nbsp;&nbsp;&nbsp;
                    </StyledWithMentorName>
                  </div>
                </StyledListItemContents>
                // </Tooltip>
              )}
            </StyledListItem>
          );
        }}
      />
    </StyledMentoringHistoryWrapper>
  );
};

export default MentoringCourseTicketHistory;

const StyledMentoringHistoryWrapper = styled.div`
  max-width: 400px;
  & .ant-list-items {
    border: 1px solid ${Colors.grey1};
    border-radius: 10px;
    padding: 0 20px;
  }
`;

const StyledListItem = styled.div`
  color: #000;
`;

const StyledListItemContents = styled.div`
  /* margin: 25px 0px; */
  padding: 15px 0px;
  border-top: 1px solid ${Colors.grey0};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.9rem;
  @media screen and ${device.lg} {
    font-size: 0.7rem;
  }
  & .created_at {
    color: ${Colors.grey3};
  }
`;

const StyledMentoringTicketName = styled.span`
  /* min-width: 300px; */
`;

const StyledWithMentorName = styled.div`
  width: 90px;
  @media screen and ${device.lg} {
    width: 75px;
  }

  float: right;
`;
